import { posToDOMRect } from '@tiptap/core';
import WriterService from 'api/ai/writerService';
import { createNamespacedHelpers } from 'vuex';
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { DOMSerializer } from 'prosemirror-model';

const corp = createNamespacedHelpers('corp');

export default {
    computed: {
        ...corp.mapGetters(['getQuotaAI', 'quotaAILimitReached']),
        getHash() {
            return `ai-${this.parentHash}`;
        },
        getInputPosition() {
            const AIInput = this.editor.$nodes('SellsyAIInput');
            return AIInput[0].pos || 0;
        },
        getCurrentEditor() {
            return this.editor ?? this.$store.state[this.getHash].editor;
        },
        getCurrentNodePosition() {
            return posToDOMRect(
                this.getCurrentEditor.view,
                this.getCurrentEditor.state.selection.from,
                this.getCurrentEditor.state.selection.to,
            );
        },
        getEditorPosition() {
            return this.getCurrentEditor.view.dom.getBoundingClientRect();
        },
        getEditorPadding() {
            return parseInt(
                window
                    .getComputedStyle(this.getCurrentEditor.view.dom, null)
                    .getPropertyValue('padding-left'),
            );
        },
    },
    methods: {
        convertTagsToHTML(text) {
            const regex = /%\{([^}]*)\}%/g;

            return text.replace(
                regex,
                (_, content) =>
                    `<span type="default" label="${content}" isdraggable="true" ishovered="false" isfocused="false" isdisabled="false" class="custom-tags-and-fields custom-tags-and-fields__default">${content}</span>`,
            );
        },
        convertTagsToMarkdown(text) {
            const regex =
                /<span[^>]*class="custom-tags-and-fields custom-tags-and-fields__default"[^>]*>([^<]*)<\/span>/g;

            const tagStart = '%{';
            const tagEnd = '}%';

            return text.replace(regex, `${tagStart}$1${tagEnd}`);
        },
        convertHTMLToMarkdown(htmlValue) {
            htmlValue = this.convertTagsToMarkdown(htmlValue);

            const turndownService = new TurndownService({
                headingStyle: 'atx',
                blankReplacement(content, node) {
                    const types = ['P'];
                    if (types.indexOf(node.nodeName) !== -1) {
                        return `&nbsp;  \n`;
                    }
                },
            });
            return turndownService.turndown(htmlValue);
        },
        convertMarkdownToHTML(markdownValue) {
            const markdown = new MarkdownIt();
            const html = markdown.render(markdownValue);
            return this.convertTagsToHTML(html);
        },
        getParentHash() {
            let iterator = 0;
            let parent = this.$parent;
            while (parent && !parent.hash && iterator < 5) {
                parent = parent?.$parent;
                iterator++;
            }
            return parent.hash;
        },
        getHTMLfromSelection(selection) {
            const slice = selection.content();
            const serializer = DOMSerializer.fromSchema(this.getCurrentEditor.schema);
            const fragment = serializer.serializeFragment(slice.content);
            const div = document.createElement('div');
            div.appendChild(fragment);

            return div.innerHTML;
        },
        getSelectedText() {
            const { view } = this.getCurrentEditor;
            const { from, to } = view.state.selection;

            if (!this.$store.state[this.getHash].originalHTML) {
                this.$store.dispatch(`${this.getHash}/setCoords`, { from, to });
            }

            return this.convertHTMLToMarkdown(this.getHTMLfromSelection(view.state.selection));
        },
        getAnchorPosition() {
            // By default, selection.from is set to 1 by Tiptap (for Focus tiptap editor)
            return this.getCurrentEditor.state.selection.to - 1;
        },
        getTextRichText() {
            const selectedText = this.getSelectedText();
            if (selectedText) {
                return selectedText;
            }
            this.getCurrentEditor.commands.selectAll();
            return this.getSelectedText();
        },
        resetStore() {
            this.$store.dispatch(`${this.getHash}/setOriginalHTML`, '');
            this.$store.dispatch(`${this.getHash}/setOriginalText`, '');
            this.$store.dispatch(`${this.getHash}/setCoords`, {
                from: 0,
                to: 0,
                loader: 0,
                input: 0,
            });
            this.$store.dispatch(`${this.getHash}/setOriginalPrompt`, {
                task: '',
                tone: null,
                language: null,
                userInput: null,
                write: null,
            });
            this.$store.dispatch(`${this.getHash}/setCancelAIGeneration`, false);
        },
        removeAIInput() {
            this.editor
                .chain()
                .focus()
                .setNodeSelection(this.getInputPosition)
                .deleteSelection()
                .run();
        },
        validateAIContent() {
            this.getCurrentEditor
                .chain()
                .focus()
                .selectAll()
                .unsetHighlight()
                .setTextSelection(this.$store.state[this.getHash].coords.to) // puts cursor at end of generated text
                .run();
            this.resetStore();
            this.toggleLoaderDisplay(false);
            this.getCurrentEditor.setEditable(true);
        },
        cancelAIContent() {
            this.$store.state[this.getHash].controller.abort();
            this.getCurrentEditor
                .chain()
                .focus()
                .setContent(this.$store.state[this.getHash].originalText)
                .focus(this.$store.state[this.getHash].coords.to)
                .run();
            this.toggleLoaderDisplay(false);
            this.$store.dispatch(`${this.getHash}/setController`);
            this.resetStore();
            this.getCurrentEditor.setEditable(true);
        },
        handleUserInput(userInput) {
            this.getCurrentEditor
                .chain()
                .focus()
                .setNodeSelection(this.getInputPosition) // deletes input
                .deleteSelection()
                .run();
            this.writerServiceAdapter({ task: 'generate', userInput });
        },
        async generatePromptTagAI(prompt) {
            const writerService = new WriterService();
            return await writerService
                .generateTag({
                    text: prompt,
                    type: 'email',
                })
                .then(async (response) => {
                    return await this.handleStreamedResponseTagAI(response);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') return;
                });
        },
        regeneratePrompt() {
            this.writerServiceAdapter({
                ...this.$store.state[this.getHash].originalPrompt,
                originalText: this.$store.state[this.getHash].originalHTML,
                isRetry: true,
            });
        },
        getLocalLanguage() {
            const languageMap = {
                fr: 'French',
                en: 'English',
                es: 'Spanish',
                de: 'Deutsch',
            };

            return languageMap[window.App.lang] || 'English';
        },
        showErrorAndResetRichText() {
            this.$pushToast({
                type: 'error',
                text: this.$l10n.__(
                    'Une erreur est survenue lors de la récupération de la réponse.',
                ),
                icon: true,
            });

            this.toggleLoaderDisplay(false);

            this.getCurrentEditor
                .chain()
                .focus()
                .setTextSelection(this.$store.state[this.getHash].coords.to) // puts cursor at end of generated text
                .run();
            this.resetStore();
            this.getCurrentEditor.setEditable(true);
        },
        updateLoaderPosition() {
            this.$store.dispatch(`${this.getHash}/setCoords`, {
                loader: {
                    x: this.getEditorPadding,
                    y: this.getCurrentNodePosition.bottom - this.getEditorPosition.top,
                },
            });
        },
        toggleLoaderState(state) {
            this.$store.dispatch(`${this.getHash}/setIsLoading`, state);
        },
        toggleLoaderDisplay(state) {
            this.$store.dispatch(`${this.getHash}/setShowLoader`, state);
        },
        async writerServiceAdapter({
            task,
            tone = null,
            language = null,
            write = null,
            originalText = null,
            userInput = null,
            isRetry = false,
        }) {
            this.$store.dispatch('corp/fetchQuotaAI');
            if (!this.$store.state[this.getHash].editor) {
                this.$store.dispatch(`${this.getHash}/setEditor`, this.editor);
            }
            this.getCurrentEditor.setEditable(false);
            const writerService = new WriterService();

            if (write && originalText === null && !isRetry) {
                this.$store.dispatch(
                    `${this.getHash}/setOriginalHTML`,
                    this.getCurrentEditor.getHTML(),
                );
                // no text, generate new text and create loader
                // Init coords from the cursor position
                this.$store.dispatch(`${this.getHash}/setCoords`, {
                    from: this.getAnchorPosition(),
                });
                this.$store.dispatch(`${this.getHash}/setOriginalPrompt`, {
                    task,
                    write,
                });
                this.getCurrentEditor.commands.focus(this.getAnchorPosition());
                this.updateLoaderPosition();
                this.toggleLoaderState(true);
                this.toggleLoaderDisplay(true);
            } else if (userInput && !isRetry) {
                this.$store.dispatch(
                    `${this.getHash}/setOriginalText`,
                    this.getCurrentEditor.getHTML(),
                );
                this.$store.dispatch(`${this.getHash}/setOriginalHTML`, this.getTextRichText());
                // user input, no text selected
                this.$store.dispatch(`${this.getHash}/setOriginalPrompt`, {
                    task,
                    tone,
                    language,
                    write,
                    userInput,
                });
                this.$store.dispatch(`${this.getHash}/setCoords`, {
                    from: this.$store.state[this.getHash].coords.input,
                });
                if (this.$store.state[this.getHash].coords.input !== 0) {
                    this.getCurrentEditor
                        .chain()
                        .focus(this.$store.state[this.getHash].coords.from)
                        .run();
                }
                this.updateLoaderPosition();
                this.toggleLoaderState(true);
                this.toggleLoaderDisplay(true);
            } else if (!write && !originalText && !isRetry) {
                // original text (selected), generate new text and create loader
                // save original text and prompt in case of retry
                this.$store.dispatch(
                    `${this.getHash}/setOriginalText`,
                    this.getCurrentEditor.getHTML(),
                );
                this.$store.dispatch(`${this.getHash}/setOriginalHTML`, this.getTextRichText());
                this.$store.dispatch(`${this.getHash}/setOriginalPrompt`, {
                    task,
                    tone,
                    language,
                    write,
                });
                // Add loader to the end of the selection

                this.getCurrentEditor
                    .chain()
                    .setTextSelection({
                        from: this.$store.state[this.getHash].coords.from,
                        to: this.$store.state[this.getHash].coords.to,
                    })
                    .run();

                this.updateLoaderPosition();
                this.toggleLoaderState(true);
                this.toggleLoaderDisplay(true);
            } else {
                // select generated text to be replaced
                this.getCurrentEditor
                    .chain()
                    .focus()
                    .setTextSelection({
                        from: this.$store.state[this.getHash].coords.from,
                        to: this.$store.state[this.getHash].coords.to,
                    })
                    .run();
                this.toggleLoaderState(true);
            }

            if (tone) {
                await writerService
                    .changeTone({
                        text: this.$store.state[this.getHash].originalHTML,
                        type: this.$store.state[this.getHash].typeRichText,
                        source: this.$store.state[this.getHash].source,
                        tone: tone,
                        signal: this.$store.state[this.getHash].controller.signal,
                        retry: isRetry,
                    })
                    .then(async (response) => {
                        await this.handleStreamedResponse({ response, isRetry });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.showErrorAndResetRichText();
                    })
                    .finally(() => {
                        this.toggleLoaderState(false);
                        this.$store.dispatch(`${this.getHash}/setController`);
                    });
            } else if (write) {
                return await writerService
                    .write({
                        type: this.$store.state[this.getHash].typeRichText,
                        source: this.$store.state[this.getHash].source,
                        task,
                        language: this.getLocalLanguage(),
                        signal: this.$store.state[this.getHash].controller.signal,
                        retry: isRetry,
                    })
                    .then(async (response) => {
                        await this.handleStreamedResponse({ response, isWrite: true, isRetry });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.showErrorAndResetRichText();
                    })
                    .finally(() => {
                        this.toggleLoaderState(false);
                        this.$store.dispatch(`${this.getHash}/setController`);
                    });
            } else if (language) {
                await writerService
                    .translate({
                        text: this.$store.state[this.getHash].originalHTML,
                        type: this.$store.state[this.getHash].typeRichText,
                        source: this.$store.state[this.getHash].source,
                        language,
                        signal: this.$store.state[this.getHash].controller.signal,
                        retry: isRetry,
                    })
                    .then(async (response) => {
                        await this.handleStreamedResponse({ response });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.showErrorAndResetRichText();
                    })
                    .finally(() => {
                        this.toggleLoaderState(false);
                        this.$store.dispatch(`${this.getHash}/setController`);
                    });
            } else if (userInput) {
                await writerService
                    .generate({
                        text: userInput,
                        type: this.$store.state[this.getHash].typeRichText,
                        source: this.$store.state[this.getHash].source,
                        signal: this.$store.state[this.getHash].controller.signal,
                        retry: isRetry,
                    })
                    .then(async (response) => {
                        await this.handleStreamedResponse({
                            response,
                            isRetry,
                            isUserInput: !!userInput,
                        });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.showErrorAndResetRichText();
                    })
                    .finally(() => {
                        this.toggleLoaderState(false);
                        this.$store.dispatch(`${this.getHash}/setController`);
                    });
            } else {
                await writerService
                    .adapt({
                        text: this.$store.state[this.getHash].originalHTML,
                        type: this.$store.state[this.getHash].typeRichText,
                        source: this.$store.state[this.getHash].source,
                        task,
                        signal: this.$store.state[this.getHash].controller.signal,
                        retry: isRetry,
                    })
                    .then(async (response) => {
                        await this.handleStreamedResponse({ response, isRetry });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.showErrorAndResetRichText();
                    })
                    .finally(() => {
                        this.toggleLoaderState(false);
                        this.$store.dispatch(`${this.getHash}/setController`);
                    });
            }
        },
        async handleStreamedResponse({
            response,
            isWrite = false,
            isUserInput = false,
            isRetry = false,
        }) {
            const reader = response.body.getReader();
            let finalText = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    this.getCurrentEditor
                        .chain()
                        .focus()
                        .setTextSelection({
                            from: this.$store.state[this.getHash].coords.from,
                            to: this.$store.state[this.getHash].coords.to,
                        })
                        .deleteSelection()
                        .insertContentAt(
                            this.$store.state[this.getHash].coords.from,
                            this.convertMarkdownToHTML(finalText).replaceAll(
                                String.fromCharCode(92),
                                '',
                            ),
                            {
                                updateSelection: true,
                            },
                        )
                        .run();

                    // separating the commands avoids an incorrect cursor position
                    this.getCurrentEditor
                        .chain()
                        .focus()
                        .setTextSelection({
                            from: this.$store.state[this.getHash].coords.from,
                            to: this.getCurrentEditor.state.selection.$anchor.pos,
                        })
                        .setHighlight({ color: '#E8DBFB' })
                        .setTextSelection(this.getCurrentEditor.state.selection.$to.pos)
                        .run();

                    this.updateLoaderPosition();

                    this.$store.dispatch(`${this.getHash}/setCoords`, {
                        to: this.getCurrentEditor.state.selection.$to.pos,
                    });
                    this.getCurrentEditor.setEditable(true);

                    // set uuid and outputAI in store for feedback
                    this.$store.dispatch(
                        `${this.getHash}/setUuid`,
                        response.headers.get('X-Request-ID'),
                    );
                    this.$store.dispatch(`${this.getHash}/setOutputAI`, finalText);
                    break;
                }

                // stops the dialog being deleted by deleteSelection
                if ((isUserInput || isWrite) && !isRetry) {
                    this.getCurrentEditor
                        .chain()
                        .focus()
                        .insertText(new TextDecoder().decode(value, { stream: true }))
                        .scrollIntoView()
                        .run();
                } else {
                    this.getCurrentEditor
                        .chain()
                        .focus()
                        .deleteSelection()
                        .setTextSelection(this.$store.state[this.getHash].coords.to)
                        .insertText(new TextDecoder().decode(value, { stream: true }))
                        .scrollIntoView()
                        .run();
                }

                finalText += new TextDecoder().decode(value, { stream: true });

                this.$store.dispatch(`${this.getHash}/setCoords`, {
                    to: this.getCurrentEditor.commands.getEndSelection(),
                });
                this.updateLoaderPosition();
            }
        },
        async handleStreamedResponseTagAI(response) {
            const reader = response.body.getReader();
            let finalText = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    return finalText;
                }
                finalText += new TextDecoder().decode(value, { stream: true });
            }
        },
    },
};
